import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {
  OrderStateManagerService,
  OrderTicket,
  PortalEvent,
  QuantityPool
} from '@tix/event-buyer/services';
import { OrderSelectBaseComponent } from '../order-select-base/order-select-base.component';
import { formatAsDollarFigure } from '@tix/shared/utils';
import { TixGetItemsAmountsSoldGQL } from '@tix/data-access';
import { debounce } from 'lodash';

@Component({
  selector: 'tix-order-items',
  templateUrl: './order-items.component.html',
  styleUrls: ['./order-items.component.scss']
})
export class TixOrderItemsComponent
  extends OrderSelectBaseComponent
  implements OnInit, OnDestroy
{
  @Output() onTicketsChange = new EventEmitter<OrderTicket[]>();

  public errorMessage: string = '';

  @Input()
  event: PortalEvent;
  @Input()
  tickets: OrderTicket[] = [];

  constructor(
    orderStateManager: OrderStateManagerService,
    private changeDetector: ChangeDetectorRef,
    getAmountsSoldQuery: TixGetItemsAmountsSoldGQL
  ) {
    super(orderStateManager, getAmountsSoldQuery, changeDetector);
  }

  dollarFigureToNumber(dollarFigure?: string) {
    if (!dollarFigure) return 0;
    return Number(dollarFigure.replace(/[^0-9.-]+/g, ''));
  }

  override async ngOnInit() {
    super.ngOnInit();

    this.orderStateManager.loadPersistedOrder();

    const newOrderItems = this.tickets.map(ticket => {
      let pool: QuantityPool | undefined = undefined;

      if (ticket.parentTicketId) {
        const poolTickets = this.tickets.filter(
          t =>
            ticket.parentTicketId === t.parentTicketId ||
            ticket.parentTicketId === t.ticketConfigId
        );
        const poolChildren = poolTickets.filter(t => !!t.parentTicketId);
        const poolParent = this.tickets.find(
          t => t.ticketConfigId === ticket.parentTicketId
        );

        pool = {
          id: poolParent?.ticketConfigId,
          quantityOnHold: 0,
          quantitySold: poolTickets.reduce(
            (total, t) => total + (t.ticketConfigTotals?.ticketsSold ?? 0),
            0
          ),
          childrenItemIds: poolChildren.map(t => t.ticketConfigId),
          quantity: poolParent?.noOfSeats ?? 0
        };
      } else {
        const poolTickets = this.tickets.filter(
          t =>
            ticket.ticketConfigId === t.parentTicketId ||
            ticket.ticketConfigId === t.ticketConfigId
        );
        const poolChildren = poolTickets.filter(t => !!t.parentTicketId);
        const poolParent = ticket;

        pool =
          poolChildren.length === 0
            ? undefined
            : {
                id: poolParent.ticketConfigId,
                quantityOnHold: 0,
                quantitySold: poolTickets.reduce(
                  (total, t) =>
                    total + (t.ticketConfigTotals?.ticketsSold ?? 0),
                  0
                ),
                childrenItemIds: poolChildren.map(t => t.ticketConfigId),
                quantity: poolParent?.noOfSeats ?? 0
              };
      }

      return {
        id: ticket.ticketConfigId,
        isPoolParent: !!pool && !ticket.parentTicketId,
        quantitySold: ticket.ticketConfigTotals?.ticketsSold ?? 0,
        totalQuantity: ticket.noOfSeats ?? 0,
        type: 'ticket',
        minPerOrder: ticket.perOrderMin,
        maxPerOrder: ticket.perOrderMax,

        startSellingDate: ticket.startSellingDate,
        stopSellingDate: ticket.stopSellingDate,
        startSellingTime: ticket.startSellingTime,
        stopSellingTime: ticket.stopSellingTime,
        pool
      };
    });

    this.orderStateManager.addOrderItems(newOrderItems);

    await this.orderStateManager.loadOrderFromHold();
    await this.startTrackingAvailability();

    this.orderSelectionInitialized = true;

    this.tickets.forEach(ticket => {
      ticket.ticketQty = this.orderStateManager.getQuantitySelectedForItem(
        ticket.ticketConfigId
      );
    });
    this.onTicketsChange.emit(this.tickets);
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.orderStateManager.removeOrderFromHold();
  }

  orderChangedHandler = debounce(() => {
    this.tickets.forEach(ticket => {
      ticket.ticketQty = this.orderStateManager.getQuantitySelectedForItem(
        ticket.ticketConfigId
      );
    });
    this.onTicketsChange.emit(this.tickets);
    this.orderStateManager.fetchHoldInformation();
    this.orderStateManager.updateAvailability();
    this.orderStateManager.updateOrderOnHold();
  }, 800);

  override async onOrderChanged(): Promise<void> {
    await super.onOrderChanged();

    this.orderChangedHandler();
  }
  getMaxQty(ticketId: string) {
    return this.orderStateManager.getMaxSelectableForItem(ticketId);
  }
  getTotal(price1: string, price2: string, price3: string) {
    return formatAsDollarFigure(
      this.dollarFigureToNumber(price1) +
        this.dollarFigureToNumber(price2) +
        this.dollarFigureToNumber(price3)
    );
  }
}
